/**
 * Capacities & Resources Module (formerly Warehouse)
 *
 * Combines:
 * - Cloud Resources: Virtual infrastructure capacity (CPU, RAM, Storage, Network)
 * - Physical Inventory: Physical equipment, racks, locations
 */

import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import { RouteObject } from 'react-router-dom'
import CapacitiesOverview from './components/Overview'
import WarehouseDashboard from './components/Dashboard'
import ItemsList from './components/Items'
import ProcessesList from './components/Processes'
import ProcessDetail from './components/Processes/ProcessDetail'
import CreateProcess from './components/Processes/CreateProcess'
import EquipmentDetail from './components/Equipment/EquipmentDetail'
import ComponentDetail from './components/Components/ComponentDetail'
import LocationsList from './components/Locations'
import LocationDetail from './components/Locations/LocationDetail'
import RacksList from './components/Racks'
import RackDetail from './components/Racks/RackDetail'
import RackForm from './components/Racks/RackForm'
import ZoneDetail from './components/CloudCapacity/ZoneDetail'
import InstanceDetail from './components/CloudCapacity/InstanceDetail'
import ModelsList from './components/Models'
import ModelDetail from './components/Models/ModelDetail'
import ModelForm from './components/Models/ModelForm'
import SuppliersList from './components/Suppliers'
import SupplierDetail from './components/Suppliers/SupplierDetail'
import SupplierForm from './components/Suppliers/SupplierForm'

const WarehouseModule: RouteObject = {
    path: 'warehouse',
    children: [
        // Landing page - Overview with tabs
        {
            path: '',
            element: <CapacitiesOverview />,
        },
        // Cloud Resources section - uses Overview with Cloud tab active
        {
            path: 'cloud',
            element: <CapacitiesOverview />,
        },
        {
            path: 'cloud/providers/:providerType',
            element: <CapacitiesOverview />, // Provider-specific view not yet implemented
        },
        {
            path: 'cloud/zones/:zoneId',
            element: <ZoneDetail />,
        },
        {
            path: 'cloud/instances/:instanceId',
            element: <InstanceDetail />,
        },
        // Physical Inventory section (existing dashboard, now at /inventory)
        {
            path: 'inventory',
            element: <WarehouseDashboard />,
        },
        // Existing routes for physical inventory management
        {
            path: 'items',
            element: <ItemsList />,
        },
        {
            path: 'locations',
            element: <LocationsList />,
        },
        {
            path: 'locations/:id',
            element: <LocationDetail />,
        },
        {
            path: 'racks',
            element: <RacksList />,
        },
        {
            path: 'racks/new',
            element: <RackForm />,
        },
        {
            path: 'racks/:id',
            element: <RackDetail />,
        },
        {
            path: 'racks/:id/edit',
            element: <RackForm />,
        },
        {
            path: 'processes',
            element: <ProcessesList />,
        },
        {
            path: 'processes/new',
            element: <CreateProcess />,
        },
        {
            path: 'processes/:id',
            element: <ProcessDetail />,
        },
        {
            path: 'processes/:id/edit',
            element: <CreateProcess />,
        },
        // Item Models management
        {
            path: 'models',
            element: <ModelsList />,
        },
        {
            path: 'models/new',
            element: <ModelForm />,
        },
        {
            path: 'models/:id',
            element: <ModelDetail />,
        },
        {
            path: 'models/:id/edit',
            element: <ModelForm />,
        },
        // Suppliers management
        {
            path: 'suppliers',
            element: <SuppliersList />,
        },
        {
            path: 'suppliers/new',
            element: <SupplierForm />,
        },
        {
            path: 'suppliers/:id',
            element: <SupplierDetail />,
        },
        {
            path: 'suppliers/:id/edit',
            element: <SupplierForm />,
        },
        {
            path: 'equipment/:id',
            element: <EquipmentDetail />,
        },
        {
            path: 'components/:id',
            element: <ComponentDetail />,
        },
    ],
    errorElement: <ErrorPage status={500} />,
}

export default WarehouseModule
